import {Role, roles} from '@/constants/role';
import {SsoMappingType} from '@/constants/sso-mapping-type';
import {userHasAtLeastOneClaim, userHasClaims} from '@/helpers/has-claim-helper';
import type {LoginRequest} from '@/models/authentication/authentication-response';
import type {NewPasswordRequest} from '@/models/authentication/new-password-request';
import type {PasswordResetRequest} from '@/models/authentication/password-reset-request';
import {
  createNewPassword,
  login,
  ssoLogin,
  passwordResetRequest,
  switchOrganization,
  me,
  logout,
  antiforgeryToken
} from '@/services/authentication-service';
import {defineStore, getActivePinia} from 'pinia';
import {useOrganizationStore} from '@/stores/organization/organization-store';
import {useSignalRStore} from '@/stores/signalr/signalr-store';
import {languageStoreId} from '@/constants/stores';
import {useUserStore} from './user/user-store';
import type {UserInfoResponse} from '@/models/authentication/authentication-response';
import {type DensitySize} from '@/constants/density-size';

/**
 * Retry schedule for confirming a freshly issued session (see confirmSessionEstablished).
 * First attempt is immediate; the rest only cost time on browsers that actually lag.
 */
const SESSION_CONFIRM_DELAYS_MS = [0, 100, 250, 500];

/** authStore.error marker: login succeeded but the session never became usable. */
export const SESSION_NOT_ESTABLISHED = 'SESSION_NOT_ESTABLISHED';

interface AuthState {
  loginRequest: LoginRequest;
  passwordResetRequest: PasswordResetRequest;
  newPasswordRequest: NewPasswordRequest;
  username: string;
  userId: string;
  organizationId: number;
  organizationName: string;
  role: Role;
  error: string;
  loading: boolean;
  claims: string[];
  tableDensity: DensitySize;
  originalOrganizationId?: number;
  hydratedFromServer: boolean;
  hydrationPromise?: Promise<boolean>;
  antiForgeryToken: string;
  antiForgeryTokenHydrated: boolean;
  antiForgeryTokenPromise?: Promise<boolean>;
  ssoMappingType: SsoMappingType;
}

export const useAuthStore = defineStore({
  id: 'authentication',
  state: () => ({
    username: '',
    userId: '',
    organizationId: 0,
    organizationName: '',
    loginRequest: {
      username: '',
      password: ''
    },
    passwordResetRequest: {
      email: ''
    },
    newPasswordRequest: {
      email: '',
      newPassword: '',
      confirmNewPassword: '',
      token: ''
    },
    role: Role.None,
    error: "",
    loading: false,
    claims: [],
    tableDensity: 'standard',
    originalOrganizationId: undefined,
    hydratedFromServer: false,
    hydrationPromise: undefined,
    antiForgeryToken: '',
    antiForgeryTokenHydrated: false,
    antiForgeryTokenPromise: undefined,
    ssoMappingType: SsoMappingType.None
  } as AuthState),

  getters: {},

  actions: {
    applyUserInfo(userInfo: UserInfoResponse | null | undefined) {
      if (!userInfo) return;

      this.username = userInfo.username ?? '';
      this.userId = userInfo.userId ?? '';
      this.organizationId = userInfo.organizationId ?? 0;
      this.organizationName = userInfo.organizationName ?? '';
      this.originalOrganizationId = userInfo.originalOrganizationId ?? 0;

      const firstRole = (userInfo.roles ?? [])[0];
      this.role = roles.get(firstRole) ?? Role.None;

      this.claims = Array.isArray(userInfo.claims) ? userInfo.claims : [];
      this.ssoMappingType = userInfo.ssoMappingType ?? SsoMappingType.None;
    },

    /**
     * Ensures the store is hydrated from the server exactly once per page load.
     * Safe to call from router guards and app startup.
     */
    async ensureHydrated(): Promise<boolean> {
      if (this.hydratedFromServer) {
        return true;
      }

      if (!this.hydrationPromise) {
        this.hydrationPromise = this.hydrateFromServer().finally(() => {
          this.hydrationPromise = undefined;
        });
      }

      const ok = await this.hydrationPromise;
      
      if (ok) {
        this.hydratedFromServer = true;
      }

      return ok;
    },

    async hydrateFromServer(): Promise<boolean> {
      try {
        const result = await me()
        if (result.status !== 200) {
          this.clearAuthState();
          return false;
        }

        this.applyUserInfo(result.data);

        // These only depend on /me's result, not on each other — fetch them
        // concurrently. The antiforgery token is prefetched (non-blocking for
        // hydration success) so the first POST, e.g. Trajectory/List, doesn't
        // pay an extra serial round trip in the http interceptor.
        const hydrationTasks: Promise<unknown>[] = [
          this.ensureAntiForgeryTokenHydrated().catch(() => false)
        ];

        if (this.userId) {
          hydrationTasks.push(
            useUserStore().getUserTableDensity(this.userId).then(density => {
              this.tableDensity = density;
            })
          );
        }

        if (this.organizationId) {
          hydrationTasks.push(useOrganizationStore().ensureOrganizationSettings(this.organizationId));
        }

        await Promise.all(hydrationTasks);

        return this.isAuthenticated();
      } catch {
        this.clearAuthState();
        return false;
      }
    },
    
    async ensureAntiForgeryTokenHydrated(forceRefresh: boolean = false): Promise<boolean> {
      if (!forceRefresh && this.antiForgeryTokenHydrated) {
        return true;
      }

      if (!this.antiForgeryTokenPromise) {
        this.antiForgeryTokenPromise = this.hydrateAntiForgeryToken().finally(() => {
          this.antiForgeryTokenPromise = undefined;
        });
      }

      const ok = await this.antiForgeryTokenPromise;
      
      if (ok) {
        this.antiForgeryTokenHydrated = true;
      }

      return ok;
    },

    async hydrateAntiForgeryToken(): Promise<boolean> {
      const antiForgeryTokenResult = await antiforgeryToken();
      this.antiForgeryToken = antiForgeryTokenResult.data.token;
      return this.antiForgeryToken !== '';
    },

    clearAuthState() {
      this.username = '';
      this.userId = '';
      this.organizationId = 0;
      this.organizationName = '';
      this.role = Role.None;
      this.claims = [];
      this.antiForgeryToken = '';
      this.antiForgeryTokenPromise = undefined;
      this.hydratedFromServer = false;
      this.antiForgeryTokenPromise = undefined;
      this.antiForgeryTokenHydrated = false;
    },
    
    async login(): Promise<boolean> {
      this.loading = true;
      try {
        const result = await login(this.loginRequest);
        return await this.afterLoginHandler(result);
      } finally {
        // In a finally: anything that throws on the way (a rejected bootstrap call,
        // a cancelled navigation) must never leave the login button spinning forever.
        this.loading = false;
      }
    },

    async ssoLogin(idToken: string): Promise<boolean> {
      this.loading = true;
      try {
        const result = await ssoLogin(idToken);
        return await this.afterLoginHandler(result);
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      await logout();
      this.clearAuthState();

      // @ts-ignore
      // Clearing the pinia stores will also clear any local storage values attached to the store
      getActivePinia()?._s.forEach(store => {
        if (storesToKeepPersisted.includes(store.$id)) return;
        store.$reset();
      });

      // Refresh the page
      this.router.go(0);
    },

    // Session-expired counterpart to logout(): no API call (the session is
    // already dead on the server — calling /authentication/logout would 401
    // and re-enter the 401 handler) and no full page reload (a reload would
    // destroy any toast we are about to show).
    async handleSessionExpired() {
      if (!this.isAuthenticated()) {
        if (this.router.currentRoute.value.name !== 'login') {
          await this.router.push({name: 'login'});
        }
        return;
      }

      this.clearAuthState();

      // @ts-ignore
      getActivePinia()?._s.forEach(store => {
        if (storesToKeepPersisted.includes(store.$id)) return;
        store.$reset();
      });

      await this.router.push({name: 'login'});
    },

    async sendPasswordResetEmail() {
      await passwordResetRequest(this.passwordResetRequest);
    },

    async createNewPassword(): Promise<boolean> {
      this.loading = true;
      const result = await createNewPassword(this.newPasswordRequest);
      this.loading = false;
      return result.status === 200;
    },

    async switchOrganization(organizationId: number) {
      this.loading = true;
      try {
        const result = await switchOrganization({organizationId: organizationId});
        if (result.status !== 200) {
          return false;
        }

        // Report the outcome of the session hand-over, not just the HTTP status:
        // the caller reloads the page on true, which is only safe once the new
        // organization's cookie is the one the server actually sees.
        return await this.afterLoginHandler(result);
      } finally {
        this.loading = false;
      }
    },

    async afterLoginHandler(result: any) {
      if (result.status !== 200) {
        return false;
      }

      this.error = '';
      this.applyUserInfo(result.data);

      if (!await this.confirmSessionEstablished(result.data)) {
        this.clearAuthState();
        this.error = SESSION_NOT_ESTABLISHED;
        return false;
      }

      this.hydratedFromServer = true;

      await this.ensureHydrated();

      await Promise.all([
          useOrganizationStore().getFeatureToggles(),
          useOrganizationStore().getOrganizationSettings(this.organizationId),
          useSignalRStore().Connect()
        ]
      );

      return true;
    },

    /**
     * Waits until the server confirms the session the login response just handed us.
     *
     * WebKit does not commit a Set-Cookie from a cross-origin XHR response in step with
     * that request resolving, so the calls we fire straight after logging in can still
     * leave without the fresh auth cookie — a 401 that the interceptor would otherwise
     * report as an expired session, bouncing the user back to login (Safari only).
     *
     * Matches on identity rather than on the status alone: after switchOrganization the
     * previous cookie is still valid for hours, so a racing /me answers 200 with the old
     * user/organization instead of failing.
     */
    async confirmSessionEstablished(expected: UserInfoResponse): Promise<boolean> {
      for (const delayMs of SESSION_CONFIRM_DELAYS_MS) {
        if (delayMs) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }

        const result = await me();
        if (result?.status === 200
          && result.data?.userId === expected.userId
          && result.data?.organizationId === expected.organizationId) {
          return true;
        }
      }

      return false;
    },

    hasClaims(claims: string[]): boolean {
      return userHasClaims(claims, this.claims);
    },
    hasAtLeastOneClaim(claims: string[]): boolean {
      return userHasAtLeastOneClaim(claims, this.claims);
    },
    isAuthenticated() {
      return this.userId !== '';
    },
    async setTableDensity(density: DensitySize) {
      this.tableDensity = density;
      await useUserStore().updateUserTableDensity(this.userId, density);
    },

    hasSwitchedOrganization(): boolean {
      return this.originalOrganizationId !== this.organizationId;
    },
  }
});

export const storesToKeepPersisted = [
  languageStoreId
]
